// Statusni i labelski metapodaci za "Promocije" i "Refundacije" tabove
// finansija - preslikano sa
// dostavljaci-front/app/utils/{promotion,refund}.ts, prilagođeno za
// Vuetify2 boje (success/warning/error/secondary/grey).
//
// Labeli se ne čuvaju kao gotov tekst (label) vec kao i18n kljuc (labelKey),
// jer su ovo plain JS module-level konstante bez pristupa this.$t().

export const PROMOTION_STATUS_META = {
  active: { labelKey: "finance_status_promotion_active", color: "success" },
  completed: { labelKey: "finance_status_promotion_completed", color: "grey" },
  planned: { labelKey: "finance_status_promotion_planned", color: "warning" },
};

export const PROMOTION_FUNDING_META = {
  ordera: "finance_funding_ordera",
  restaurant: "finance_funding_restaurant",
  shared: "finance_funding_shared",
};

export const PAYOUT_STATUS_META = {
  paid: { labelKey: "finance_status_payout_paid", color: "success" },
  pending: { labelKey: "finance_status_payout_pending", color: "warning" },
  processing: { labelKey: "finance_status_payout_processing", color: "secondary" },
};

export const REFUND_STATUS_META = {
  approved: { labelKey: "finance_status_refund_approved", color: "success" },
  pending: { labelKey: "finance_status_refund_pending", color: "warning" },
  rejected: { labelKey: "finance_status_refund_rejected", color: "grey" },
};

// "Tok novca" / money_flow - dijeljeno između Transakcija taba (kolona + legenda)
// i Pregleda (rezime aktivnosti). Vidi ticket #223693 / #223694.
// Boje: crvena = restoran duguje, zelena = Ordera duguje, plava = plaćeno direktno.
export const MONEY_FLOW_META = {
  RESTAURANT_OWES_ORDERA: { color: "#d9534f", labelKey: "finance_mf_restaurant_owes" },
  ORDERA_OWES_RESTAURANT: { color: "#00b37e", labelKey: "finance_mf_ordera_owes" },
  DIRECT_PAYMENT: { color: "#2f6fed", labelKey: "finance_mf_direct_payment" },
  ORDER_VALUE: { color: "#9aa4b2", labelKey: "finance_mf_order_value" },
  // Retko - samo kad je ugovor sa firmom za dostavu podešen da restoran
  // direktno plaća naknadu dostave (odvojeno od RESTAURANT_OWES_ORDERA,
  // primalac je firma za dostavu, ne Ordera). Vidi backend izvještaj 13.09.2026.
  RESTAURANT_OWES_DELIVERY_COMPANY: {
    color: "#d9820f",
    labelKey: "finance_mf_owes_delivery_company",
  },
};

// Isti kodovi kao Knjiga salda (description) - vidi ticket #223672 v2.
// Dijeljeno između Transakcija taba (filter/kolona) i Pregleda (Nedavne
// aktivnosti) - oba prikazuju sirovi entry_type sa backenda i treba im isti
// prevod. Redoslijed ključeva određuje redoslijed opcija u filteru na
// Transakcije tabu.
export const TRANSACTION_TYPE_LABEL_KEYS = {
  ORDER_REVENUE: "finance_desc_order_revenue",
  COMMISSION: "finance_desc_commission",
  CASH_RECEIVED: "finance_desc_cash_received",
  CASH_ADVANCE: "finance_desc_cash_advance",
  PAYOUT: "finance_desc_payout",
  SUBSCRIPTION: "finance_desc_subscription",
  REFUND: "finance_desc_refund",
};

// currency defaults to "KM" only as a last-resort fallback; callers should
// pass the restaurant's configured currency (see global `restaurantCurrency` mixin in main.js).
export function formatCurrency(value, currency = "KM") {
  const num = Number(value);
  return `${Number.isFinite(num) ? num.toFixed(2) : "0.00"} ${currency}`;
}

// Lokalni (ne UTC) YYYY-MM-DD za ?from=/?to= parametre. date.toISOString() NE
// koristiti za ovo - konvertuje u UTC, pa lokalna ponoć u UTC+1/+2 zonama
// (Bosna/Srbija/Hrvatska) upadne u PRETHODNI dan (npr. 1.1. postane 31.12.).
// Vidi network checklist F13, 14.09.2026.
export function toDateParam(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("sr-RS", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// 4 ekrana finansija (Zarada / Obračuni / Transakcije / Ugovor i cijene) -
// docs/2026/10/14_10_2026_Backend_specifikacija_finansije_4_ekrana.md
// ---------------------------------------------------------------------------

// Novac ima smjer i boju (princip 3): zeleno = u korist restorana, crveno = na
// teret, sivo = prošlo kroz ruke restorana (keš), plavo = korekcija.
export const FLOW_COLORS = {
  positive: "#00b37e",
  negative: "#d9534f",
  cash: "#9aa4b2",
  correction: "#2f6fed",
  owing: "#d9820f",
};

// income_vs_costs[].kind -> predznak + boja. Nepoznat kind pada na predznak vrijednosti.
// "cost" i "cash" se uvijek prikazuju kao odbitak (backend smije poslati i apsolutnu vrijednost).
export function signedStatementValue(row) {
  const value = Number(row.value) || 0;
  if (row.kind === "cost" || row.kind === "cash") return -Math.abs(value);
  return value;
}

export function statementRowColor(row) {
  if (row.kind === "cash") return FLOW_COLORS.cash;
  if (row.kind === "correction") return FLOW_COLORS.correction;
  return signedStatementValue(row) >= 0 ? FLOW_COLORS.positive : FLOW_COLORS.negative;
}

// Dinamika isplate (contract.payout_schedule) -> i18n ključ.
export const PAYOUT_SCHEDULE_LABEL_KEYS = {
  DAILY: "finance_schedule_daily",
  WEEKLY_7: "finance_schedule_weekly_7",
  WEEKLY_15: "finance_schedule_weekly_15",
  MONTHLY: "finance_schedule_monthly",
  THRESHOLD: "finance_schedule_threshold",
};

export const CASH_FLOW_MODEL_LABEL_KEYS = {
  COURIER_PAYS_ON_PICKUP: "finance_cash_model_courier_pays",
  ORDERA_SETTLES_LATER: "finance_cash_model_ordera_settles",
};

// Datum sljedećeg obračuna računa front. Poznat je samo za dnevnu i mjesečnu
// dinamiku - sedmične/15-dnevne zavise od sidra koje front ne zna, a prag od salda.
export function nextSettlementDate(schedule, now = new Date()) {
  if (schedule === "DAILY") {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  }
  if (schedule === "MONTHLY") {
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }
  return null;
}

// Izbor perioda: preset -> { from, to } (YYYY-MM-DD, lokalno vrijeme).
export const PERIOD_PRESETS = ["this_month", "last_month", "last_30", "last_90", "this_year"];

export function resolvePeriodPreset(preset, now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case "last_month":
      return { from: toDateParam(new Date(y, m - 1, 1)), to: toDateParam(new Date(y, m, 0)) };
    case "last_30":
      return { from: toDateParam(new Date(y, m, now.getDate() - 29)), to: toDateParam(now) };
    case "last_90":
      return { from: toDateParam(new Date(y, m, now.getDate() - 89)), to: toDateParam(now) };
    case "this_year":
      return { from: toDateParam(new Date(y, 0, 1)), to: toDateParam(now) };
    case "this_month":
    default:
      return { from: toDateParam(new Date(y, m, 1)), to: toDateParam(now) };
  }
}

export function defaultPeriod() {
  return { preset: "this_month", ...resolvePeriodPreset("this_month") };
}

// "1.264,50 KM" - valuta uvijek iz odgovora/restorana (princip 6), nikad konstanta.
export function formatMoney(value, currency, { signed = false } = {}) {
  const num = Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  const text = Math.abs(safe).toLocaleString("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = safe < 0 ? "-" : signed && safe > 0 ? "+" : "";
  return `${sign}${text}${currency ? ` ${currency}` : ""}`;
}

// daily_turnover ne vraća dane sa 0 prometa - popuni ih za odabrani opseg.
export function fillDailySeries(points, from, to) {
  const byDay = new Map((points || []).map((p) => [String(p.label).slice(0, 10), Number(p.value) || 0]));
  if (!from || !to) return (points || []).map((p) => ({ label: p.label, value: Number(p.value) || 0 }));
  const result = [];
  const cursor = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  // Zaštita od ogromnog opsega (npr. "godina") - tad crtamo samo dane sa prometom.
  if ((end - cursor) / 86400000 > 120) {
    return [...byDay.entries()].map(([label, value]) => ({ label, value }));
  }
  while (cursor <= end) {
    const key = toDateParam(cursor);
    result.push({ label: key, value: byDay.get(key) || 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
}

// Kartice događaja na Transakcijama (source_type != ORDER) - boja trake lijevo + opis smjera (caption).
export const EVENT_SOURCE_META = {
  RESTAURANT_PAYOUT: { labelKey: "finance_event_payout", captionKey: "finance_event_caption_payout", color: "#1d1d1f" },
  PAYOUT: { labelKey: "finance_event_payout", captionKey: "finance_event_caption_payout", color: "#1d1d1f" },
  PAYMENT_RECEIVED: { labelKey: "finance_event_payment", captionKey: "finance_event_caption_payment", color: FLOW_COLORS.positive },
  SUBSCRIPTION: { labelKey: "finance_event_subscription", captionKey: "finance_event_caption_subscription", color: FLOW_COLORS.negative },
  RESTAURANT_REFUND: { labelKey: "finance_event_refund", captionKey: "finance_event_caption_refund", color: FLOW_COLORS.correction },
  REVERSAL: { labelKey: "finance_event_reversal", captionKey: "finance_event_caption_reversal", color: FLOW_COLORS.correction },
};
